import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import "./Transport.css";

interface TransportItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Transport: React.FC = () => {
  const [data, setData] = useState<TransportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/transport`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching transport:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: TransportItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: TransportItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return `${ServerUrl}/storage/A1Image/placeholder.png`;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Transport",
    de: "Verkehrsmittel",
    ta: "போக்குவரத்து",
    si: "ප්‍රවාහනය",
    hi: "परिवहन",
    ar: "وسائل النقل",
    tr: "Ulaşım",
    ru: "Транспорт",
    zh: "交通工具",
    fr: "Transport",
    es: "Transporte",
  };

  const subtitleMap = {
    en: "Transport in German",
    de: "Verkehrsmittel auf Deutsch",
    ta: "ஜெர்மன் மொழியில் போக்குவரத்து",
    si: "ජර්මන් භාෂාවෙන් ප්‍රවාහනය",
    hi: "जर्मन में परिवहन",
    ar: "وسائل النقل بالألمانية",
    tr: "Almanca'da Ulaşım",
    ru: "Транспорт на немецком",
    zh: "德语交通工具",
    fr: "Transport en allemand",
    es: "Transporte en alemán",
  };

  return (
    <div className="transport-page-container">
      <div className="transport-page-header">
        <div>
          <h1 className="transport-page-title">{titleMap["de"]}</h1>
          <p className="transport-page-subtitle">{subtitleMap[language]}</p>
        </div>
      </div>

      {loading ? (
        <div className="transport-page-loading">Loading...</div>
      ) : (
        <div className="transport-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            return (
              <div
                key={item.id}
                className={`transport-page-card ${
                  isPlaying ? "transport-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="transport-page-image-wrapper">
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.german}
                    className="transport-page-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `${ServerUrl}/storage/A1Image/placeholder.png`;
                    }}
                  />
                </div>
                <div className="transport-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="transport-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="transport-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Transport;
